import { useState } from 'react';
import { anexoService } from '../../../services/anexo';
import { demandaService } from '../../../services/demanda';
import { receivedDemandsService } from '../../../services/receivedDemands';
import { useEventEmitter } from '../../../hooks/useEventBus';
import { toast } from '../../../components/Toast';
import type { Anexo } from '../../../models';

interface UseDemandAttachmentsProps {
  demandaId: number;
  refetchHistorico: () => void;
  showAlert: (type: 'info' | 'warning' | 'error' | 'success', message: string, title?: string) => void;
  showConfirm: (type: 'info' | 'warning' | 'danger' | 'success', message: string, onConfirm: () => void, details?: string, title?: string) => void;
  setCommentAttachModal: React.Dispatch<React.SetStateAction<{ isOpen: boolean; isMinimized: boolean; isLoading: boolean }>>;
}

export const useDemandAttachments = ({
  demandaId,
  refetchHistorico,
  showAlert,
  showConfirm,
  setCommentAttachModal
}: UseDemandAttachmentsProps) => {
  // Hook para emitir eventos
  const { emit } = useEventEmitter();
  
  // Helper para buscar demanda atualizada
  const fetchUpdatedDemanda = async () => {
    try {
      const demandaData = await receivedDemandsService.getDemanda(demandaId);
      return demandaData;
    } catch (error) {
      console.warn('Erro ao buscar demanda atualizada:', error);
      return null;
    }
  };
  
  // Estados para comentar e anexar
  const [comment, setComment] = useState('');
  const [attachments, setAttachments] = useState<File[]>([]);
  const [attachmentComments, setAttachmentComments] = useState<Record<string, string>>({});
  const [isDragOver, setIsDragOver] = useState(false);

  // Função para visualizar anexo (via Blob autenticado)
  const handleViewAttachment = async (anexo: Anexo) => {
    try {
      if (!anexo.anexo) {
        showAlert('warning', 'Caminho do anexo não encontrado.', 'Anexo indisponível');
        return;
      }

      // Baixar como blob autenticado e abrir em nova aba
      const objectUrl = await anexoService.viewAsBlob(anexo);
      const newWindow = window.open(objectUrl, '_blank');
      if (!newWindow) {
        // Se bloqueado por popup, tentar fallback abrindo via mesma aba
        window.location.href = objectUrl;
      }

      // Opcional: revogar URL depois de um tempo para liberar memória
      setTimeout(() => URL.revokeObjectURL(objectUrl), 600_000); // 10 minutos
    } catch (error) {
      console.error('Erro ao visualizar anexo:', error);
      // Fallback: tentar abrir URL direta
      try {
        const url = anexoService.getViewUrl(anexo);
        window.open(url, '_blank');
      } catch (fallbackError) {
        console.error('Erro no fallback ao abrir URL direta do anexo:', fallbackError);
      }
      showAlert('error', 'Não foi possível visualizar o anexo agora.', 'Erro de Visualização');
    }
  };

  // Função para fazer download do anexo
  const handleDownloadAttachment = async (anexo: Anexo) => {
    try {
      if (!anexo.anexo) {
        showAlert('warning', 'Caminho do anexo não encontrado.', 'Download indisponível');
        return;
      }

      // Usar o serviço de anexos para fazer o download
      await anexoService.download(anexo);
    } catch (error) {
      console.error('Erro ao fazer download do anexo:', error);
      showAlert('error', 'Erro ao fazer download do anexo.', 'Erro de Download');
    }
  };

  // Função para deletar anexo
  const handleDeleteAttachment = (anexo: Anexo) => {
    showConfirm(
      'danger',
      `Tem certeza que deseja remover o anexo "${anexo.nome_anexo}" desta demanda?`,
      async () => {
        try {
          // Alinhar com o backend: remover anexos é feito via demandaService.removeranexos
          await demandaService.removeranexos(demandaId, [anexo.id]);

          // Buscar demanda atualizada
          const updatedDemanda = await fetchUpdatedDemanda();

          // Emitir evento de atualização com demanda atualizada
          emit('demand-updated', {
            demandId: demandaId,
            changes: {
              type: 'attachment-removed',
              attachments: [anexo.id],
              updatedDemanda
            }
          });

          // Mostrar toast de sucesso
          toast.success('Anexo removido com sucesso!');
          
          // Recarregar histórico para atualizar a lista de anexos
          refetchHistorico();
        } catch (error) {
          console.error('Erro ao remover anexo:', error);
          const status = (error as any)?.response?.status as number | undefined;
          if (status === 403) {
            showAlert('error', 'Você não tem permissão para remover este anexo. Apenas o autor do anexo ou um administrador pode removê-lo.', 'Acesso negado');
          } else if (status === 404) {
            showAlert('warning', 'Anexo não encontrado ou já foi removido.', 'Não encontrado');
          } else {
            showAlert('error', 'Erro ao remover anexo. Tente novamente.', 'Erro');
          }
        }
      },
      'Esta ação não pode ser desfeita.',
      'Confirmar Remoção'
    );
  };

  // Funções para manipular anexos no modal de comentário
  const handleAddFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      
      const allowedTypes = [
        'application/pdf',
        'image/jpeg', 'image/jpg', 'image/png', 'image/gif',
        'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'text/plain',
        'application/zip', 'application/x-rar-compressed'
      ];
      
      const validFiles: File[] = [];
      
      newFiles.forEach(file => {
        // Verificar se o arquivo já existe
        const fileExists = attachments.some(existingFile => 
          existingFile.name === file.name && existingFile.size === file.size
        );
        
        if (fileExists) {
          return; // Pular arquivo duplicado
        }
        
        // Verificar tamanho do arquivo (máx. 30MB)
        const maxSize = 30 * 1024 * 1024; // 30MB em bytes
        if (file.size > maxSize) {
          showAlert('warning', `Arquivo "${file.name}" é muito grande. Tamanho máximo permitido: 30MB.`, 'Arquivo muito grande');
          return;
        }
        
        const fileExtension = file.name.toLowerCase().split('.').pop();
        const allowedExtensions = ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'doc', 'docx', 'xls', 'xlsx', 'txt', 'zip', 'rar'];
        
        if (allowedTypes.includes(file.type) || allowedExtensions.includes(fileExtension || '')) {
          validFiles.push(file);
        } else {
          showAlert('warning', `Tipo de arquivo "${file.name}" não permitido.`, 'Tipo não permitido');
        }
      });
      
      // Adicionar aos arquivos existentes
      setAttachments(prev => [...prev, ...validFiles]);
      
      // Inicializar comentários vazios para os novos arquivos
      const newComments: Record<string, string> = {};
      validFiles.forEach(file => {
        const fileKey = `${file.name}_${file.size}_${Date.now()}`;
        newComments[fileKey] = '';
      });
      setAttachmentComments(prev => ({ ...prev, ...newComments }));
      
      // Limpar o input
      e.target.value = '';
    }
  };

  // Handler para remover arquivo individual
  const handleRemoveFile = (indexToRemove: number) => {
    const fileToRemove = attachments[indexToRemove];
    if (fileToRemove) {
      const fileKey = `${fileToRemove.name}_${fileToRemove.size}`;
      // Remover o arquivo e seu comentário
      setAttachments(prev => prev.filter((_, index) => index !== indexToRemove));
      setAttachmentComments(prev => {
        const newComments = { ...prev };
        // Procurar e remover a chave que corresponde a este arquivo
        Object.keys(newComments).forEach(key => {
          if (key.startsWith(fileKey)) {
            delete newComments[key];
          }
        });
        return newComments;
      });
    }
  };

  // Handlers para drag and drop
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Só remove o estado se realmente sair da área
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsDragOver(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      // Simular evento de input para reutilizar a lógica existente
      const fakeEvent = {
        target: {
          files: files
        }
      } as any;
      handleAddFiles(fakeEvent);
    }
  };

  // Função para submeter comentário com anexos
  const handleSubmitCommentAttach = async () => {

    if (!demandaId) {
      showAlert('error', 'ID da demanda não encontrado', 'Erro');
      return;
    }

    // Validar se há comentário ou anexos
    if (!comment?.trim() && (!attachments || attachments.length === 0)) {
      showAlert('warning', 'É necessário adicionar um comentário ou pelo menos um anexo.', 'Atenção');
      return;
    }

    try {
      // Atualizar estado para mostrar loading
      setCommentAttachModal(prev => ({ ...prev, isLoading: true }));

      // Preparar dados dos anexos com comentários
      const attachmentsData = attachments.map((file) => {
        // Procurar a chave correspondente a este arquivo
        const fileKeyPattern = `${file.name}_${file.size}`;
        const matchingKey = Object.keys(attachmentComments).find(key => 
          key.startsWith(fileKeyPattern)
        );
        const fileComment = matchingKey ? attachmentComments[matchingKey] : '';
        
        return {
          file,
          comment: fileComment
        };
      });

      // Verificar se usará anexar com comentários ou apenas comentar
      if (attachments && attachments.length > 0) {
        // Tem anexos - usar função de anexar com comentários
        const attachmentCount = attachments.length;
        const response = await demandaService.anexarComComentarios(
          demandaId,
          attachmentsData,
          comment?.trim() || undefined
        );
        // Buscar demanda atualizada
        const updatedDemanda = await fetchUpdatedDemanda();
        
        // Emitir evento de atualização da demanda com dados atualizados
        emit('demand-updated', {
          demandId: demandaId,
          changes: {
            type: 'attachment-added',
            attachments: response.anexos || [],
            attachmentCount,
            updatedDemanda
          }
        });
        
        // Atualizar histórico
        refetchHistorico();
        
        // Fechar modal e limpar estados
        setCommentAttachModal({ isOpen: false, isMinimized: false, isLoading: false });
        setComment('');
        setAttachments([]);
        setAttachmentComments({});
        setIsDragOver(false);
        
        // Mostrar toast de sucesso
        const attachmentText = attachmentCount === 1 ? 'arquivo anexado' : `${attachmentCount} arquivos anexados`;
        toast.success(`${attachmentText} com sucesso!`);
        
      } else if (comment?.trim()) {
        // Apenas comentário - usar função de comentar
        await demandaService.comentar(demandaId, comment?.trim() || '');

        // Buscar demanda atualizada
        const updatedDemanda = await fetchUpdatedDemanda();
        
        // Emitir evento de comentário adicionado com dados atualizados
        emit('demand-updated', {
          demandId: demandaId,
          changes: {
            type: 'comment-added',
            comment: comment.trim(),
            updatedDemanda
          }
        });
        
        // Atualizar histórico
        refetchHistorico();
        
        // Fechar modal e limpar estados
        setCommentAttachModal({ isOpen: false, isMinimized: false, isLoading: false });
        setComment('');
        setAttachments([]);
        setAttachmentComments({});
        setIsDragOver(false);
        
        // Mostrar toast de sucesso
        toast.success('Comentário adicionado com sucesso!');
      }
      
    } catch (error) {
      console.error('Erro ao submeter comentário/anexos:', error);
      
      // Remover loading em caso de erro
      setCommentAttachModal(prev => ({ ...prev, isLoading: false }));
      
      showAlert('error', 'Erro ao enviar comentário ou anexos. Tente novamente.', 'Erro');
    }
  };

  // Função para fechar modal de comentário e anexo e limpar estados
  const handleCloseCommentAttachModal = () => {
    setComment('');
    setAttachments([]);
    setAttachmentComments({});
    setIsDragOver(false);
    setCommentAttachModal({ isOpen: false, isMinimized: false, isLoading: false });
  };

  return {
    // Estados
    comment,
    setComment,
    attachments,
    setAttachments,
    attachmentComments,
    setAttachmentComments,
    isDragOver,
    setIsDragOver,
    
    // Funções para anexos existentes
    handleViewAttachment,
    handleDownloadAttachment,
    handleDeleteAttachment,
    
    // Funções para manipular anexos no modal
    handleAddFiles,
    handleRemoveFile,
    handleDragEnter,
    handleDragLeave,
    handleDragOver,
    handleDrop,
    handleSubmitCommentAttach,
    handleCloseCommentAttachModal
  };
};